import React, { memo, useState } from 'react';
import { css } from 'styled-components';
import MagicSliderDots from 'react-magic-slider-dots';
import { checkValue, LightenDarkenColor } from 'src/components/common/helper';
import { SelectBox } from 'src/components/common/modules/form/select-box';

// Components
import Layout from 'src/components/common/theme/Layout';
import { Typo } from 'src/components/common/ui';
import { SlickSlider } from 'src/components/common/modules/carousel/slick/slick-carousel';
import DealCardByRegion from './deal-card-by-region';
import DealCardByIds from './deal-card-by-ids';
import DealCardByCampaignIds from './deal-card-by-campaignIds';
import { MONTH_FILTER_KEY, TEAM_FILTER_KEY } from './deal-fixtures';

const DealFixturesView: React.FC = memo((props: any) => {
  const {
    dealCardList,
    backgroundColor,
    headline,
    filterName,
    textColor,
    ctaColor,
    ctaTextColor,
    dealCtaText,
    ctaText,
    headlineTextColor,
    hotelCardInfo,
    resetSearchResults,
    mccidData,
    id,
    key,
    handleFilter,
    initialSlideCount,
    sliderRef,
    filters,
    monthFilterOptions,
    teamFilterOptions,
    selectFilters,
    filterNameTeam
  } = props;

  const [activeSlide, setActiveSlide] = useState(initialSlideCount);

  const settings = {
    arrows: true,
    slidesToShow: 3,
    slidesToScroll: 3,
    dots: true,
    infinite: dealCardList?.length > 3,
    beforeChange: (_: number, next: number) => setActiveSlide(next + 3),
    appendDots: (dots) => {
      return <MagicSliderDots dots={dots} numDotsToShow={3} dotWidth={30} />;
    },
    responsive: [
      {
        breakpoint: 992,
        settings: {
          arrows: true,
          slidesToShow: 2,
          slidesToScroll: 2,
          dots: true,
          infinite: dealCardList?.length > 2,
          beforeChange: (_: number, next: number) => setActiveSlide(next + 2),
        },
      },
      {
        breakpoint: 575,
        settings: {
          arrows: true,
          slidesToShow: 1,
          slidesToScroll: 1,
          dots: true,
          infinite: dealCardList?.length > 1,
          beforeChange: (_: number, next: number) => setActiveSlide(next + 1),
          appendDots: (dots) => {
            return <MagicSliderDots dots={dots} numDotsToShow={3} dotWidth={22} />;
          },
        },
      },
    ],
  };

  // Dynamic style overrides
  const moduleCss = () => {
    const darkenColor = checkValue(headlineTextColor) ? LightenDarkenColor(headlineTextColor, -0.1) : '';
    return `
      ${
        checkValue(backgroundColor)
          ? `
          .deal-fixtures-outer-wrapper {
            background-color: ${backgroundColor};
          }
        `
          : ''
      }
      ${
        checkValue(textColor)
          ? `
          .deal-fixtures-headline {
            color: ${textColor} !important;
          }
          .deal-fixtures-slider-container .slick-dots .slick-active button:before {
            color: ${textColor} !important;
          }
          .input-box {
            background-color: ${textColor};
          }
          .slide-count {
            color: ${textColor};
          }
        `
          : ''
      }
      ${
        checkValue(headlineTextColor)
          ? `
          .deal-fixtures-headline {
            color: ${headlineTextColor} !important;
          }
          .slide-count {
            color: ${headlineTextColor} !important;
          }
          .deal-fixtures-slider-container .slick-dots .slick-active button:before {
            color: ${headlineTextColor} !important;
          }
          .slick-slider .slick-arrow {
            background-color: ${headlineTextColor} !important;
          }
          .slick-slider .slick-arrow:hover {
            background-color: ${darkenColor} !important;
          }
          .slick-slider .slick-disabled {
            background-color: #94949E !important;
          }
          .slick-slider .slick-disabled:before {
            border-color: ${headlineTextColor} !important;
          }
        `
          : ''
      }
      ${
        checkValue(headlineTextColor) && checkValue(textColor)
          ? `
          .input-box {
            background-color: ${headlineTextColor} !important;
            color: ${textColor} !important;
          }
          .input-box option {
            color: ${textColor} !important;
          }
          .uitk-field-select-icon {
            fill: ${textColor} !important;
          }
          .slick-slider .slick-arrow:before {
            border-color: ${textColor} !important;
          }
        `
          : ''
      }
    `;
  };

  const mixedContentTiles = () =>
    checkValue(dealCardList, true) &&
    dealCardList.map((el) => {
      const dealsProps = {
        ...el,
        textColor,
        ctaColor,
        ctaTextColor,
        resetSearchResults,
        ctaText,
        dealCtaText,
        mccidData,
        hotelCardInfo,
        id
      };
      if (checkValue(el?.hotelIds) || checkValue(el?.hotelIdsSecondRow))
        return <DealCardByIds key={el.uniquekey} {...dealsProps} />
      if (checkValue(el?.campaignId))
        return <DealCardByCampaignIds key={el.uniquekey} {...dealsProps} />
      return <DealCardByRegion key={el.uniquekey} {...dealsProps} />
    });

  const handleFilterClick = (e, type: string) => {
    handleFilter(e, type);
    setTimeout(() => {
      setActiveSlide(initialSlideCount);
    }, 5);
  };

  const slideCount = () => {
    const currentSlideSet = Math.floor(activeSlide / initialSlideCount) + (activeSlide % initialSlideCount);
    const fullSlideSet = Math.floor(dealCardList?.length / initialSlideCount) + ((dealCardList?.length % initialSlideCount) !== 0 ? 1 : 0);
    return `${
      activeSlide <= dealCardList?.length ? currentSlideSet : fullSlideSet
    } / ${fullSlideSet}`
  }

  return (
    <Layout
      css={css`
        ${moduleCss()}
      `}
      className="deal-fixtures"
      HtmlTag="section"
      id={checkValue(id) ? id : key?.next()}      
    >
      <div className="deal-fixtures-outer-wrapper">
        <div className={`container deal-fixtures-wrapper
        ${
          checkValue(dealCardList, true) && dealCardList?.length <= initialSlideCount && 
          !checkValue(selectFilters, true) ? 'deal-fixtures-wrapper--without-carosuel' : ''
        }`}>
          <Typo.HTMLText tag="h2" exClass="deal-fixtures-headline">
            {headline}
          </Typo.HTMLText>
          <div className="deal-fixtures-select-input-container">
            {checkValue(selectFilters, true) &&
              selectFilters.map((el: string) => {
                if (el === 'Month')
                  return (
                    <SelectBox
                      id={`deal-fixtures-select-input-month-${checkValue(id) ? id : key?.next()}`}
                      label={''}
                      value={filters[MONTH_FILTER_KEY]}
                      options={monthFilterOptions}
                      initialOption={{ label: checkValue(filterName) ? filterName : '', value: '' }}
                      exClass={`input-box entryform-input tiny-select-input`}
                      gaTracking={{ eventCategory: '', eventAction: '', eventLabel: '' }}
                      invalid={''}
                      onChangeEvent={(_, e) => handleFilterClick(e, MONTH_FILTER_KEY)}
                      hasVisibleLabel={false}
                      hasBorder={false}
                    />
                  );
                if (el === 'Team')
                  return (
                    <SelectBox
                      id={`deal-fixtures-select-input-title-${checkValue(id) ? id : key?.next()}`}
                      label={''}
                      value={filters[TEAM_FILTER_KEY]}
                      options={teamFilterOptions}
                      initialOption={{ label: checkValue(filterNameTeam) ? filterNameTeam : '', value: '' }}
                      exClass={`input-box entryform-input tiny-select-input`}
                      gaTracking={{ eventCategory: '', eventAction: '', eventLabel: '' }}
                      invalid={''}
                      onChangeEvent={(_, e) => handleFilterClick(e, TEAM_FILTER_KEY)}
                      hasVisibleLabel={false}
                      hasBorder={false}
                    />
                  );
                else return null;
              })}
          </div>
          <div className="deal-fixtures-slider-container">
            {checkValue(dealCardList, true) && dealCardList?.length > initialSlideCount && (
              <div className="slide-count">{slideCount()}</div>
            )}
            <SlickSlider key={JSON.stringify(filters)} ref={sliderRef} settings={settings}>
              {mixedContentTiles()}
            </SlickSlider>
          </div>
        </div>
      </div>
    </Layout>
  );
});

DealFixturesView.displayName = 'DealFixturesView';

export default DealFixturesView;
